import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// Imports of the module

import { parseDate } from "@/lib/utils";
import i18n from "@/i18n";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { categoryOptions } from "./filter";

import { LockClosedIcon, PersonIcon } from "@radix-ui/react-icons";

// Type of the fields in the table

export type User = {
  id: number;
  email: string;
  tableau_username: string | null;
  status: number;
  category: number | null;
  company_text: string | null;
  group_user: string | null;
  created_at: string;
  confirmed_at: string | null;
  last_active_at: string | null;
  last_sign_in_at: string | null;
  sign_in_count: number;
  invitation_accepted_at: string | null;
  locked_at?: string | null;
  approved?: boolean;
};

// Definition of the columns

export const users_columns: ColumnDef<User>[] = [
  {
    accessorKey: "id",
    header: "Id",
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => <span className="text-xs">{row.getValue("email")}</span>,
  },
  {
    accessorKey: "tableau_username",
    header: "Tableau Username",
    cell: ({ row }) => (
      <span className="text-xs">{row.getValue("tableau_username") || ""}</span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      // Example status mapping, customize as needed
      const value = row.getValue("status");
      let label = "Unknown";
      switch (value) {
        case 0:
          label = "Inactive";
          break;
        case 1:
          label = "Active";
          break;
        default:
          if (typeof value === "string" && value.length > 0) label = value;
          break;
      }
      return <span className="text-xs">{label}</span>;
    },
  },
  {
    accessorKey: "category",
    header: "Category",
    cell: ({ row }) => {
      // Convert category number to string label using categoryOptions from filter.tsx
      const value = row.getValue("category");

      // Si category est null, undefined ou 0, ne rien afficher
      if (value === null || value === undefined || Number(value) === 0) {
        return <span className="text-xs"></span>;
      }

      // Find the matching option based on the value
      const option = categoryOptions[Number(value) - 1];
      return (
        <span className="text-xs">{option ? option.label : String(value)}</span>
      );
    },
  },
  {
    accessorKey: "company_text",
    header: "Company Text",
    cell: ({ row }) => (
      <span className="text-xs">{row.getValue("company_text") || ""}</span>
    ),
  },
  {
    accessorKey: "group_user",
    header: "Group User",
    cell: ({ row }) => (
      <span className="text-xs">{row.getValue("group_user") || ""}</span>
    ),
  },
  {
    accessorKey: "created_at",
    header: "Created At",
    cell: ({ row }) => (
      <span className="text-xs">{parseDate(row.getValue("created_at"))}</span>
    ),
  },
  {
    accessorKey: "confirmed_at",
    header: "Confirmed At",
    cell: ({ row }) => {
      const value = row.getValue("confirmed_at");
      return value ? (
        <span className="text-xs">{parseDate(value as string)}</span>
      ) : (
        <span className="text-xs"></span>
      );
    },
  },
  {
    accessorKey: "last_active_at",
    header: "Last Active At",
    cell: ({ row }) => {
      const value = row.getValue("last_active_at");
      return value ? (
        <span className="text-xs">{parseDate(value as string)}</span>
      ) : (
        <span className="text-xs"></span>
      );
    },
  },
  {
    accessorKey: "last_sign_in_at",
    header: "Last Sign In At",
    cell: ({ row }) => {
      const value = row.getValue("last_sign_in_at");
      return value ? (
        <span className="text-xs">{parseDate(value as string)}</span>
      ) : (
        <span className="text-xs"></span>
      );
    },
  },
  {
    accessorKey: "sign_in_count",
    header: "Sign In Count",
    cell: ({ row }) => <div>{Number(row.getValue("sign_in_count"))}</div>,
  },
  {
    accessorKey: "invitation_accepted_at",
    header: "Invitation Accepted At",
    cell: ({ row }) => {
      const value = row.getValue("invitation_accepted_at");
      return value ? (
        <span className="text-xs">{parseDate(value as string)}</span>
      ) : (
        <span className="text-xs"></span>
      );
    },
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => {
      const id = row.getValue("id");
      const lockedAt = (row.original as User).locked_at;
      const approved = (row.original as User).approved;

      return (
        <div className="flex gap-2 justify-end">
          {/* Unlock access button if locked_at is present */}
          {!!lockedAt && (
            <Link
              href={toWorkspacePath(`/admin/users/${id}/unlock_access`)}
              className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-gray-50 transition-colors"
              underline="hover"
            >
              <LockClosedIcon className="w-4 h-4" />
              Unlock Access
            </Link>
          )}

          {/* Approve button if not approved */}
          {approved === false && (
            <Link
              href={toWorkspacePath(`/admin/users/${id}/approve`)}
              className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-gray-50 transition-colors"
              underline="hover"
              data-method="patch"
              onClick={(e) => {
                if (!window.confirm("Are you sure?")) e.preventDefault();
              }}
            >
              <PersonIcon className="w-4 h-4" />
              Approve
            </Link>
          )}

          {/* Edit button */}
          <Link
            href={`${window.location.pathname}/${id}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
